// Esperando nota de crédito (2026-10-19): las facturas aceptadas con algo reclamado al proveedor (faltó,
// llegó roto, se devolvió) cuyo monto todavía no cubre una NC. Ese monto queda retenido del pago; cuando
// la NC llega y se registra contra la factura, sale de esta lista sola.
import { useState } from 'react';
import { PanelError } from '../comun/Estados';
import { Modal } from '../comun/Modal';
import { fecha, pesos } from '../comun/formato';
import { useSesion } from '../sesion/SesionContexto';
import { useDocumentosCxp, type DocumentoProveedor } from '../tesoreria/datosPagos';
import { hoy } from './comun';
import { useAccionCompras } from './datos';

export function EsperandoNc({
  alAbrirFactura,
}: {
  alAbrirFactura: (documentoId: string, titulo: string) => void;
}) {
  const { empresa, puede } = useSesion();
  const q = useDocumentosCxp(empresa!.id, { abiertos: true });
  const [registrando, setRegistrando] = useState<DocumentoProveedor | null>(null);
  const filas = (q.data ?? []).filter((d) => Number(d.retenido ?? 0) > 0);
  const total = filas.reduce((s, d) => s + Number(d.retenido ?? 0), 0);

  return (
    <div className="pantalla consulta" data-pantalla="esperando-nc">
      <p className="apagado">
        Facturas con algo reclamado al proveedor (faltó, llegó roto o se devolvió). Lo reclamado
        queda retenido del pago hasta que llegue la nota de crédito; al registrarla contra la
        factura, sale de esta lista.
      </p>
      {q.isError && <PanelError error={q.error} />}
      {q.isLoading && <p className="apagado">Cargando…</p>}
      {q.isSuccess && !filas.length && (
        <p className="apagado">No hay facturas esperando nota de crédito.</p>
      )}
      {filas.length > 0 && (
        <table className="grilla compacta" aria-label="Facturas esperando nota de crédito">
          <thead>
            <tr>
              <th>Proveedor</th>
              <th>Factura</th>
              <th>Emisión</th>
              <th>Qué se reclamó</th>
              <th className="der">Total factura</th>
              <th className="der">Retenido (espera NC)</th>
              <th className="der">Se puede pagar</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {filas.map((d) => (
              <tr key={d.documentoId}>
                <td>{d.proveedor}</td>
                <td>
                  <button
                    type="button"
                    className="enlace mono"
                    onClick={() => alAbrirFactura(d.documentoId, `FCC ${d.folio ?? ''}`)}
                  >
                    {d.folio ?? '—'}
                  </button>
                </td>
                <td>{fecha(d.fechaEmision)}</td>
                <td>
                  {(d.reclamos ?? []).map((r, i) => (
                    <div key={i}>
                      {r.descripcion ?? r.tipo}
                      {r.motivo ? <span className="apagado"> · {r.motivo}</span> : null}
                    </div>
                  ))}
                </td>
                <td className="der">{pesos(d.montoTotal)}</td>
                <td className="der tono-aviso">
                  <b>{pesos(d.retenido ?? '0')}</b>
                </td>
                <td className="der">{pesos(d.pagable ?? d.disponible)}</td>
                <td>
                  {puede('compras.nc.crear') && (
                    <button
                      type="button"
                      className="boton-mini"
                      title="La NC todavía no llega por XML: regístrala con su folio"
                      onClick={() => setRegistrando(d)}
                    >
                      Registrar NC
                    </button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
          {filas.length > 1 && (
            <tfoot>
              <tr>
                <td colSpan={5}>
                  <b>Total</b> · {filas.length} facturas
                </td>
                <td className="der">
                  <b>{pesos(String(total))}</b>
                </td>
                <td />
                <td />
              </tr>
            </tfoot>
          )}
        </table>
      )}
      {registrando && (
        <RegistrarNc
          empresaId={empresa!.id}
          doc={registrando}
          alCerrar={() => setRegistrando(null)}
        />
      )}
    </div>
  );
}

/**
 * La NC que todavía no llega por XML: con su folio y fecha se arma con lo que esperaba (el detalle ya
 * se sabe). Si el Registro de Compras del SII ya la tiene, se usan sus montos. Se cruza sola si cuadra.
 */
function RegistrarNc({
  empresaId,
  doc,
  alCerrar,
}: {
  empresaId: string;
  doc: DocumentoProveedor;
  alCerrar: () => void;
}) {
  const accion = useAccionCompras(empresaId);
  const [folio, setFolio] = useState('');
  const [emision, setEmision] = useState(hoy());
  const [resultado, setResultado] = useState<{ cruzada: boolean; motivo?: string } | null>(null);
  return (
    <Modal
      titulo={`Registrar NC de ${doc.proveedor}`}
      alCerrar={alCerrar}
      ancho="angosto"
      pie={
        resultado ? (
          <button type="button" className="primario" onClick={alCerrar}>
            Cerrar
          </button>
        ) : (
          <>
            <button type="button" onClick={alCerrar}>
              Cancelar
            </button>
            <button
              type="button"
              className="primario"
              disabled={!/^\d{1,12}$/.test(folio) || accion.isPending}
              onClick={() =>
                accion.mutate(
                  {
                    ruta: '/compras/notas/desde-esperando',
                    cuerpo: {
                      empresaId,
                      proveedorEntidadId: doc.proveedorEntidadId,
                      folio: Number(folio),
                      fechaEmision: emision,
                      ...(doc.folio ? { facturaFolio: doc.folio } : {}),
                    },
                  },
                  { onSuccess: (x) => setResultado(x as { cruzada: boolean; motivo?: string }) },
                )
              }
            >
              Registrar y cruzar
            </button>
          </>
        )
      }
    >
      {resultado ? (
        <p className={resultado.cruzada ? 'tono-ok' : 'tono-aviso'}>
          {resultado.cruzada
            ? 'NC registrada y cruzada: se liberó lo retenido de la factura.'
            : `NC registrada, pero no se cruzó sola: ${resultado.motivo ?? ''}`}
        </p>
      ) : (
        <>
          <p className="ayuda">
            Factura N° {doc.folio ?? '—'}: se espera NC por {pesos(doc.retenido ?? '0')} (con IVA).
            Con el folio se arma con lo que faltó o se reclamó; si el Registro de Compras del SII ya
            la tiene, se usan sus montos.
          </p>
          <label className="campo">
            <span className="etiqueta">Folio de la NC</span>
            <input
              aria-label="Folio de la NC"
              inputMode="numeric"
              value={folio}
              onChange={(e) => setFolio(e.target.value.replace(/\D/g, ''))}
            />
          </label>
          <label className="campo">
            <span className="etiqueta">Fecha de emisión</span>
            <input
              type="date"
              aria-label="Fecha de emisión de la NC"
              value={emision}
              onChange={(e) => setEmision(e.target.value || hoy())}
            />
          </label>
        </>
      )}
      {accion.isError && <PanelError error={accion.error} />}
    </Modal>
  );
}
